"use client";

import { c as _c } from "react/compiler-runtime";
import type * as React from "react";
import CloseIcon from "@/assets/svg/close-icon.svg";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "./dialog";
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from "./sheet";
interface ResponsiveDialogTitleProps {
  children?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  modal?: boolean;
}
interface ResponsiveDialogTitleContentProps {
  className?: string;
  children?: React.ReactNode;
  onInteractOutside?: (event: Event) => void;
}
interface ResponsiveDialogTitleHeaderProps {
  className?: string;
  children?: React.ReactNode;
}
interface ResponsiveDialogTitleFooterProps {
  className?: string;
  children?: React.ReactNode;
}
interface ResponsiveDialogTitleTitleProps {
  className?: string;
  children?: React.ReactNode;
}
interface ResponsiveDialogTitleDescriptionProps {
  className?: string;
  children?: React.ReactNode;
}
interface ResponsiveDialogTitleTriggerProps {
  className?: string;
  children?: React.ReactNode;
  asChild?: boolean;
}
function ResponsiveDialogTitle(t0) {
  const $ = _c(11);
  let children;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      children,
      modal: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
    $[3] = t1;
  } else {
    children = $[1];
    props = $[2];
    t1 = $[3];
  }
  const modal = t1 === undefined ? true : t1;
  const isMobile = useIsMobile();
  if (isMobile) {
    let t2;
    if ($[4] !== children || $[5] !== props) {
      t2 = <Sheet {...props}>{children}</Sheet>;
      $[4] = children;
      $[5] = props;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    return t2;
  }
  let t2;
  if ($[7] !== children || $[8] !== modal || $[9] !== props) {
    t2 = <Dialog modal={modal} {...props}>{children}</Dialog>;
    $[7] = children;
    $[8] = modal;
    $[9] = props;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}
function ResponsiveDialogTitleTrigger(t0) {
  const $ = _c(9);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  const isMobile = useIsMobile();
  if (isMobile) {
    let t1;
    if ($[3] !== children || $[4] !== props) {
      t1 = <SheetTrigger {...props}>{children}</SheetTrigger>;
      $[3] = children;
      $[4] = props;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    return t1;
  }
  let t1;
  if ($[6] !== children || $[7] !== props) {
    t1 = <DialogTrigger {...props}>{children}</DialogTrigger>;
    $[6] = children;
    $[7] = props;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
}
function ResponsiveDialogTitleContent(t0) {
  const $ = _c(20);
  let children;
  let className;
  let onInteractOutside;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      onInteractOutside,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = onInteractOutside;
    $[4] = props;
  } else {
    children = $[1];
    className = $[2];
    onInteractOutside = $[3];
    props = $[4];
  }
  const isMobile = useIsMobile();
  if (isMobile) {
    let t1;
    if ($[5] !== className) {
      t1 = cn("px-5 pb-5 focus:outline-none h-[80dvh] gap-0", className);
      $[5] = className;
      $[6] = t1;
    } else {
      t1 = $[6];
    }
    let t2;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <SheetClose className="absolute right-3 top-3 z-10"><CloseIcon className="size-5.5" /><span className="sr-only">Close</span></SheetClose>;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    let t3;
    if ($[8] !== children || $[9] !== onInteractOutside || $[10] !== props || $[11] !== t1) {
      t3 = <SheetContent side="bottom" showCloseButton={false} className={t1} onInteractOutside={onInteractOutside} {...props}>{t2}{children}</SheetContent>;
      $[8] = children;
      $[9] = onInteractOutside;
      $[10] = props;
      $[11] = t1;
      $[12] = t3;
    } else {
      t3 = $[12];
    }
    return t3;
  }
  let t1;
  if ($[13] !== className) {
    t1 = cn("pt-0 px-5 pb-5 md:max-w-[484px] gap-0", className);
    $[13] = className;
    $[14] = t1;
  } else {
    t1 = $[14];
  }
  let t2;
  if ($[15] !== children || $[16] !== onInteractOutside || $[17] !== props || $[18] !== t1) {
    t2 = <DialogContent className={t1} onInteractOutside={onInteractOutside} {...props}>{children}</DialogContent>;
    $[15] = children;
    $[16] = onInteractOutside;
    $[17] = props;
    $[18] = t1;
    $[19] = t2;
  } else {
    t2 = $[19];
  }
  return t2;
}
function ResponsiveDialogTitleHeader(t0) {
  const $ = _c(16);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  const isMobile = useIsMobile();
  if (isMobile) {
    let t1;
    if ($[4] !== className) {
      t1 = cn("pt-3 pb-4 px-0 active: focus:outline-none", className);
      $[4] = className;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    let t2;
    if ($[6] !== children || $[7] !== props || $[8] !== t1) {
      t2 = <SheetHeader className={t1} {...props}>{children}</SheetHeader>;
      $[6] = children;
      $[7] = props;
      $[8] = t1;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    return t2;
  }
  let t1;
  if ($[10] !== className) {
    t1 = cn("pt-11 pb-4 px-0 active: focus:outline-none", className);
    $[10] = className;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  let t2;
  if ($[12] !== children || $[13] !== props || $[14] !== t1) {
    t2 = <DialogHeader className={t1} {...props}>{children}</DialogHeader>;
    $[12] = children;
    $[13] = props;
    $[14] = t1;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  return t2;
}
function ResponsiveDialogTitleFooter(t0) {
  const $ = _c(12);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  const isMobile = useIsMobile();
  if (isMobile) {
    let t1;
    if ($[4] !== children || $[5] !== className || $[6] !== props) {
      t1 = <SheetFooter className={className} {...props}>{children}</SheetFooter>;
      $[4] = children;
      $[5] = className;
      $[6] = props;
      $[7] = t1;
    } else {
      t1 = $[7];
    }
    return t1;
  }
  let t1;
  if ($[8] !== children || $[9] !== className || $[10] !== props) {
    t1 = <DialogFooter className={className} {...props}>{children}</DialogFooter>;
    $[8] = children;
    $[9] = className;
    $[10] = props;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  return t1;
}
function ResponsiveDialogTitleTitle(t0) {
  const $ = _c(19);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  const isMobile = useIsMobile();
  if (isMobile) {
    let t1;
    if ($[4] !== className) {
      t1 = cn("text-left font-['DINPro'] text-bold text-base leading-none focus:outline-none", className);
      $[4] = className;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    let t2;
    if ($[6] !== children || $[7] !== props || $[8] !== t1) {
      t2 = <SheetTitle className={t1} {...props}>{children}</SheetTitle>;
      $[6] = children;
      $[7] = props;
      $[8] = t1;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    let t3;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div className="mt-2 h-px bg-gray-200" />;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    let t4;
    if ($[11] !== t2) {
      t4 = <>{t2}{t3}</>;
      $[11] = t2;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    return t4;
  }
  let t1;
  if ($[13] !== className) {
    t1 = cn("text-center line-clamp-2 font-['DINPro'] text-bold text-base md:text-lg leading-none focus:outline-none", className);
    $[13] = className;
    $[14] = t1;
  } else {
    t1 = $[14];
  }
  let t2;
  if ($[15] !== children || $[16] !== props || $[17] !== t1) {
    t2 = <DialogTitle className={t1} {...props}>{children}</DialogTitle>;
    $[15] = children;
    $[16] = props;
    $[17] = t1;
    $[18] = t2;
  } else {
    t2 = $[18];
  }
  return t2;
}
function ResponsiveDialogTitleDescription(t0) {
  const $ = _c(12);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  const isMobile = useIsMobile();
  if (isMobile) {
    let t1;
    if ($[4] !== children || $[5] !== className || $[6] !== props) {
      t1 = <SheetDescription className={className} {...props}>{children}</SheetDescription>;
      $[4] = children;
      $[5] = className;
      $[6] = props;
      $[7] = t1;
    } else {
      t1 = $[7];
    }
    return t1;
  }
  let t1;
  if ($[8] !== children || $[9] !== className || $[10] !== props) {
    t1 = <DialogDescription className={className} {...props}>{children}</DialogDescription>;
    $[8] = children;
    $[9] = className;
    $[10] = props;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  return t1;
}
export { ResponsiveDialogTitle, ResponsiveDialogTitleContent, ResponsiveDialogTitleDescription, ResponsiveDialogTitleFooter, ResponsiveDialogTitleHeader, ResponsiveDialogTitleTitle, ResponsiveDialogTitleTrigger };